import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, CheckCircle2, LayoutGrid, MapPin, Phone } from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { CalendarView } from "@/components/portal/CalendarView";
import { BookVisitModal } from "@/components/portal/QuickActions";
import { Card, EmptyState, Spinner, StatusBadge, fmtDay } from "@/components/portal/shared";

export default function VisitsPage() {
  const { api } = usePortalAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("list");
  const [items, setItems] = useState(null);
  const [reschedule, setReschedule] = useState(null);

  const load = () => api.get("/appointments").then((r) => setItems(r.data.appointments)).catch(() => setItems([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (items === null) return <Spinner />;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = items.filter((l) => (l.appointment?.date || "") >= today);
  const past = items.filter((l) => (l.appointment?.date || "") < today);

  const complete = async (l) => {
    await api.patch(`/leads/${l.id}`, { status: "Home Visit Completed" });
    await load();
  };

  const Row = ({ l }) => (
    <li className="rounded-2xl border border-warm-200 bg-white p-4 shadow-sm" data-testid={`visit-row-${l.id}`}>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <span className="rounded-xl bg-navy-900 text-white px-3 py-2 text-sm font-semibold min-w-20 text-center shrink-0">
          {fmtDay(l.appointment?.date)}<span className="block text-[10px] font-normal">{l.appointment?.time || "—"}</span>
        </span>
        <span className="min-w-40">
          <Link to={`/portal/customers/${l.id}?tab=visits`} className="block text-sm font-semibold text-navy-900 hover:underline">{l.firstName} {l.lastName}</Link>
          <span className="block text-xs text-slate-500">{l.interest || "General enquiry"}</span>
        </span>
        <span className="text-xs text-slate-600 flex items-center gap-1"><MapPin className="h-3 w-3" /> {[l.address1, l.town, l.postcode].filter(Boolean).join(", ") || "—"}</span>
        {l.phone && <a href={`tel:${l.phone}`} data-testid={`visit-call-${l.id}`} className="inline-flex items-center gap-1.5 text-xs text-navy-900 font-medium"><Phone className="h-3.5 w-3.5" /> {l.phone}</a>}
        <span className="ml-auto"><StatusBadge s={l.status} simple /></span>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {l.status !== "Home Visit Completed" && (
          <button type="button" data-testid={`visit-complete-${l.id}`} onClick={() => complete(l)} className="inline-flex items-center gap-1 rounded-full bg-navy-900 px-3.5 py-1.5 text-[11px] font-semibold text-white hover:bg-navy-800">
            <CheckCircle2 className="h-3 w-3" /> Mark Complete
          </button>
        )}
        <button type="button" data-testid={`visit-reschedule-${l.id}`} onClick={() => setReschedule(l)} className="inline-flex items-center gap-1 rounded-full border border-warm-300 px-3.5 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-navy-800/40">
          Reschedule
        </button>
      </div>
    </li>
  );

  return (
    <div data-testid="visits-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{upcoming.length} upcoming · {past.length} past</p>
        <div className="flex rounded-full border border-warm-300 bg-white p-1" role="tablist" aria-label="View mode">
          {[["list", CheckCircle2, "List"], ["calendar", LayoutGrid, "Calendar"]].map(([m, Icon, label]) => (
            <button key={m} type="button" data-testid={`visits-mode-${m}`} onClick={() => setMode(m)} aria-pressed={mode === m}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${mode === m ? "bg-navy-900 text-white" : "text-slate-500 hover:text-navy-900"}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>

      {mode === "calendar" ? (
        <CalendarView api={api} onOpenLead={(l) => navigate(`/portal/customers/${l.id}?tab=visits`)} />
      ) : items.length === 0 ? (
        <EmptyState icon={CalendarDays}>No visits scheduled yet. Book one from a lead or the dashboard quick actions.</EmptyState>
      ) : (
        <>
          <Card title="Upcoming" testid="visits-upcoming">
            {upcoming.length === 0 ? <p className="text-sm text-slate-400">Nothing coming up.</p> : <ul className="space-y-2.5">{upcoming.map((l) => <Row key={l.id} l={l} />)}</ul>}
          </Card>
          {past.length > 0 && (
            <Card title="Past Visits" testid="visits-past">
              <ul className="space-y-2.5 opacity-75">{past.map((l) => <Row key={l.id} l={l} />)}</ul>
            </Card>
          )}
        </>
      )}

      {reschedule && <BookVisitModal lead={reschedule} onClose={() => setReschedule(null)} onDone={load} />}
    </div>
  );
}
